import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, Smartphone } from 'lucide-react';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [nightMode, setNightMode] = useState(false);

  useEffect(() => {
    setNightMode(document.body.classList.contains('dark') || document.documentElement.classList.contains('dark'));
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after 3 seconds if not dismissed before
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show manual install instructions
    if (isIOSDevice) {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response: ${outcome}`);

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom"
      data-testid="install-prompt"
    >
      <div 
        className="sketch-border p-4 shadow-lg"
        style={{
          background: `var(--bg-secondary)`,
          color: `var(--text-primary)`
        }}
      >
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 rounded-full hover:bg-accent"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-4 mb-3">
          <div className="p-3 rounded-full" style={{ background: 'var(--accent-1)' }}>
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-semibold mb-1">
              Install StoryTime Studio
            </h3>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {isIOS 
                ? 'Add to your home screen for quick access!'
                : 'Install our app for a better experience with offline access!'}
            </p>
          </div>
        </div>

        {isIOS ? (
          <div className="text-sm space-y-2 mb-3 pl-11">
            <p style={{ color: 'var(--text-secondary)' }}>
              Tap the <span className="font-semibold">Share</span> button, then
              <span className="font-semibold"> Add to Home Screen</span>
            </p>
          </div>
        ) : (
          <Button
            onClick={handleInstallClick}
            className="w-full font-sketch text-white"
            style={{
              background: 'var(--accent-2)',
              borderColor: 'var(--sketch-border)',
              color: 'white'
            }}
            size="sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Install App
          </Button>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;
